/**
 * Phase 279 — Edit Repair Distribution dialog.
 *
 * Opens from BuildingDetails.js inside each repair card. Lets the user:
 *   • Remove apartments from the distribution
 *   • Replace an apartment with another
 *   • Add missing apartments
 *   • Edit share % per row
 *   • See live allocated amounts as they type
 *
 * Backend: PUT /buildings/{buildingId}/repairs/{repairId}/distribution
 * Audit history: GET .../distribution-audit
 *
 * Safety rules enforced by the backend (we just surface the messages):
 *   • Apartments with already-paid invoices cannot be removed.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Trash, Plus, Warning, ClockCounterClockwise, X } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const round2 = (n) => Math.round(n * 100) / 100;

export default function EditRepairDistributionDialog({
  open,
  onOpenChange,
  buildingId,
  repair,        // full repair object: { repair_id, amount, apartment_distribution, ... }
  apartments,    // every apartment of the building
  onSaved,
}) {
  const { t } = useLanguage();
  const repairAmount = round2(parseFloat(repair?.amount || 0));

  // Phase 801 — Working copy of rows.
  //   share_percentage: user-visible share (0-100)
  //   allocated_amount: user-visible € allocated (primary source of truth)
  // Both are stored independently. `updateShare` recomputes allocated
  // amount and `updateAllocation` recomputes share, but the allocated
  // amount is what's persisted so the user can type any € figure without
  // rounding-roundtrip drift.
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditRows, setAuditRows] = useState([]);

  useEffect(() => {
    if (!open || !repair) return;
    const initial = (repair.apartment_distribution || []).map(d => {
      const pct = round2(parseFloat(d.share_percentage || 0));
      // Prefer stored allocated_amount when available; else derive from %.
      const rawAlloc = parseFloat(d.allocated_amount);
      const alloc = Number.isFinite(rawAlloc) && rawAlloc > 0
        ? round2(rawAlloc)
        : round2(repairAmount * pct / 100);
      return {
        apartment_id: d.apartment_id,
        share_percentage: pct,
        allocated_amount: alloc,
        _was_paid: round2(parseFloat(d.paid_amount || 0)) > 0,
      };
    });
    setRows(initial);
    setShowAudit(false);
  }, [open, repair, repairAmount]);

  const usedIds = useMemo(() => new Set(rows.map(r => r.apartment_id)), [rows]);
  const availableApartments = useMemo(
    () => (apartments || []).filter(a => !a.is_archived && !usedIds.has(a.apartment_id)),
    [apartments, usedIds]
  );
  const totalPct = useMemo(
    () => round2(rows.reduce((s, r) => s + (parseFloat(r.share_percentage) || 0), 0)),
    [rows]
  );
  const totalAlloc = useMemo(
    () => round2(rows.reduce((s, r) => s + (parseFloat(r.allocated_amount) || 0), 0)),
    [rows]
  );
  // Phase 801 — Primary balance check is now on the € total (source of
  // truth). Allow a 1-cent rounding tolerance.
  const balanced = Math.abs(totalAlloc - repairAmount) < 0.01;

  const aptMap = useMemo(
    () => Object.fromEntries((apartments || []).map(a => [a.apartment_id, a])),
    [apartments]
  );

  const addRow = (apt_id) => {
    if (!apt_id) return;
    setRows(prev => [...prev, { apartment_id: apt_id, share_percentage: 0, allocated_amount: 0, _was_paid: false }]);
  };

  const removeRow = (apt_id) => {
    const row = rows.find(r => r.apartment_id === apt_id);
    if (row?._was_paid) {
      toast.error(t('repairDistCannotRemovePaid') || 'This apartment already has payments. Refund before removing.');
      return;
    }
    setRows(prev => prev.filter(r => r.apartment_id !== apt_id));
  };

  // Phase 801 — Editing share % re-derives the € amount immediately so
  // the two columns stay in sync while the user is thinking in %.
  const updateShare = (apt_id, val) => {
    const raw = parseFloat(val);
    const num = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
    setRows(prev => prev.map(r => r.apartment_id === apt_id
      ? { ...r, share_percentage: num, allocated_amount: round2(repairAmount * num / 100) }
      : r));
  };

  // Phase 801 — Direct allocated-amount editing. Amount is the primary
  // source of truth; we back-compute a display % so the two columns stay
  // in sync, but we DO NOT round-trip the € value through the % (that's
  // what made the field feel non-editable in Phase 279).
  const updateAllocation = (apt_id, val) => {
    const raw = parseFloat(val);
    const amt = Number.isFinite(raw) ? Math.max(0, Math.min(repairAmount || 0, raw)) : 0;
    const pct = repairAmount > 0 ? round2((amt / repairAmount) * 100) : 0;
    setRows(prev => prev.map(r => r.apartment_id === apt_id
      ? { ...r, share_percentage: pct, allocated_amount: amt }
      : r));
  };

  const swapApartment = (oldId, newId) => {
    if (!newId || newId === oldId) return;
    if (usedIds.has(newId)) {
      toast.error(t('apartmentAlreadyInDistribution') || 'Apartment already in the distribution.');
      return;
    }
    const row = rows.find(r => r.apartment_id === oldId);
    if (row?._was_paid) {
      toast.error(t('repairDistCannotSwapPaid') || 'Cannot swap an apartment with existing payments. Refund first.');
      return;
    }
    setRows(prev => prev.map(r => r.apartment_id === oldId ? { ...r, apartment_id: newId } : r));
  };

  const distributeEqually = () => {
    if (rows.length === 0) return;
    const each = round2(100 / rows.length);
    setRows(prev => prev.map((r, i) => {
      const pct = i === prev.length - 1
        ? round2(100 - each * (prev.length - 1))
        : each;
      return { ...r, share_percentage: pct, allocated_amount: round2(repairAmount * pct / 100) };
    }));
  };

  // Phase 801 — Recalculate € amounts from the current share % values.
  // Useful after the user has manually tweaked € amounts and wants to
  // snap them back to the exact proportional split defined by the %
  // column.
  const recalcByPercentages = () => {
    if (rows.length === 0) return;
    setRows(prev => prev.map(r => ({
      ...r,
      allocated_amount: round2(repairAmount * (parseFloat(r.share_percentage) || 0) / 100),
    })));
    toast.success(t('recalculatedByPercentages') || 'Amounts recalculated from shares');
  };

  const loadAudit = async () => {
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/repairs/${repair.repair_id}/distribution-audit`);
      setAuditRows(Array.isArray(r.data) ? r.data : []);
      setShowAudit(true);
    } catch {
      setAuditRows([]);
      setShowAudit(true);
    }
  };

  const handleSave = async () => {
    // Phase 280 — Pre-save validation with explicit error messages.
    if (rows.length === 0) {
      toast.error(t('addAtLeastOneApartment') || 'Add at least one apartment.');
      return;
    }
    // No duplicates
    const ids = rows.map(r => r.apartment_id);
    const dupIds = ids.filter((id, i) => ids.indexOf(id) !== i);
    if (dupIds.length > 0) {
      const dupNumbers = dupIds.map(id => `#${aptMap[id]?.apartment_number || '?'}`).join(', ');
      toast.error(`${t('duplicateApartments') || 'Duplicate apartments'}: ${dupNumbers}`);
      return;
    }
    // No empty / unknown rows
    const emptyRow = rows.find(r => !r.apartment_id);
    if (emptyRow) {
      toast.error(t('emptyApartmentRow') || 'Every row must have an apartment.');
      return;
    }
    // No zero-amount rows (would create €0 invoices)
    const zeroRow = rows.find(r => round2(parseFloat(r.allocated_amount) || 0) <= 0);
    if (zeroRow) {
      toast.error(`${t('zeroAmountNotAllowed') || 'Amount must be greater than 0 for'} #${aptMap[zeroRow.apartment_id]?.apartment_number || '?'}`);
      return;
    }
    if (!balanced) {
      toast.error(`${t('allocationSumMustEqualTotal') || 'Allocations must sum to the repair total'} · ${t('current') || 'current'}: €${totalAlloc.toFixed(2)} / €${repairAmount.toFixed(2)}`);
      return;
    }

    try {
      setSaving(true);
      const payload = {
        distribution: rows.map(r => ({
          apartment_id: r.apartment_id,
          share_percentage: round2(r.share_percentage),
          allocated_amount: round2(r.allocated_amount),
        })),
      };
      const res = await axios.put(`${API}/buildings/${buildingId}/repairs/${repair.repair_id}/distribution`, payload);
      const { removed_count, added_count, changed_count } = res.data || {};
      toast.success(`${t('distributionUpdated') || 'Distribution updated'} · ${t('added') || 'Added'}: ${added_count} · ${t('removed') || 'Removed'}: ${removed_count} · ${t('changed') || 'Changed'}: ${changed_count}`);
      onOpenChange?.(false);
      onSaved?.();
    } catch (err) {
      // Phase 280 — Surface the exact backend error to the user instead of
      // the generic fallback. Backend can return:
      //   • `detail` = string  → use directly
      //   • `detail` = { message, locked: [...] }  → show message + locked apt list
      //   • network failure → use err.message
      const detail = err?.response?.data?.detail;
      let msg;
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.message || JSON.stringify(detail);
        if (Array.isArray(detail.locked) && detail.locked.length > 0) {
          const names = detail.locked
            .map(l => `#${l.apartment_number || '?'} (${l.invoice_number || '—'}: €${(l.paid_amount || 0).toFixed(2)})`)
            .join(', ');
          msg = `${msg} → ${names}`;
        }
      } else {
        msg = err?.message || (t('errorOccurred') || 'Error');
      }
      // Log full error for devs / debugging
      // eslint-disable-next-line no-console
      console.error('[EditRepairDistribution] Save failed', { status: err?.response?.status, detail, err });
      toast.error(msg, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="edit-repair-distribution-dialog">
        <DialogHeader className="border-b border-zinc-200 pb-4">
          <DialogTitle className="text-2xl font-light tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('editDistribution') || 'Edit Distribution'} — {repair?.description}
          </DialogTitle>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            {t('repairAmount') || 'Repair amount'}: <span className="font-bold text-zinc-800">€{repairAmount.toFixed(2)}</span>
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-semibold">{t('apartments')}</Label>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={distributeEqually} className="rounded-none text-xs" data-testid="dist-equally-btn">
                {t('distributeEqually') || 'Distribute equally'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={recalcByPercentages} className="rounded-none text-xs" data-testid="dist-recalc-pct-btn">
                {t('recalculateByPercentages') || 'Recalculate by %'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={loadAudit} className="rounded-none text-xs" data-testid="show-audit-btn">
                <ClockCounterClockwise size={14} className="mr-1" />{t('history') || 'History'}
              </Button>
            </div>
          </div>

          {/* Edit rows */}
          <div className="border border-zinc-200 max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase">{t('apartment')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 uppercase">{t('ownerName')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 uppercase">{t('sharePercentage')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500 uppercase">{t('allocatedAmount')} (&euro;)</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-zinc-500 uppercase w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map((row) => {
                  const apt = aptMap[row.apartment_id];
                  return (
                    <tr key={row.apartment_id} className="bg-white" data-testid={`dist-edit-row-${row.apartment_id}`}>
                      <td className="px-3 py-2">
                        <Select value={row.apartment_id} onValueChange={(v) => swapApartment(row.apartment_id, v)} disabled={row._was_paid}>
                          <SelectTrigger className="rounded-none h-8 text-sm font-semibold w-32" data-testid={`apt-select-${row.apartment_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none max-h-72">
                            <SelectItem value={row.apartment_id}>
                              #{apt?.apartment_number || '?'}
                            </SelectItem>
                            {availableApartments.map(a => (
                              <SelectItem key={a.apartment_id} value={a.apartment_id}>
                                #{a.apartment_number} — {a.owner_name || ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {row._was_paid && (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200" title={t('hasPaymentsLocked') || 'Has payments — locked'}>
                            <Warning size={9} weight="bold" /> {t('hasPayments') || 'Has payments'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-zinc-500 text-xs">{apt?.owner_name || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number" step="0.01" min="0" max="100"
                          value={row.share_percentage}
                          onChange={(e) => updateShare(row.apartment_id, e.target.value)}
                          className="w-20 ml-auto rounded-none font-mono text-sm h-8 text-right"
                          data-testid={`share-${row.apartment_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number" step="0.01" min="0" max={repairAmount}
                          value={row.allocated_amount}
                          onChange={(e) => updateAllocation(row.apartment_id, e.target.value)}
                          className="w-28 ml-auto rounded-none font-mono text-sm h-8 text-right font-semibold"
                          data-testid={`alloc-${row.apartment_id}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(row.apartment_id)}
                          className="text-zinc-400 hover:text-red-600 disabled:opacity-30"
                          disabled={row._was_paid}
                          title={row._was_paid ? (t('hasPaymentsLocked') || 'Has payments — locked') : (t('remove') || 'Remove')}
                          data-testid={`remove-${row.apartment_id}`}
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-zinc-400 py-6">{t('noApartments') || 'No apartments'}</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-300 bg-zinc-100 font-semibold">
                  <td className="px-3 py-2 text-right text-xs uppercase text-zinc-500" colSpan={2}>{t('total')}</td>
                  <td className={`px-3 py-2 text-right font-mono ${Math.abs(totalPct - 100) < 0.5 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="dist-total-pct">{totalPct.toFixed(2)}%</td>
                  <td className={`px-3 py-2 text-right font-mono ${balanced ? 'text-emerald-700' : 'text-red-600'}`} data-testid="dist-total-alloc">
                    €{totalAlloc.toFixed(2)}
                    <div className="text-[10px] font-normal">
                      {balanced
                        ? <span className="text-emerald-600">✓ = €{repairAmount.toFixed(2)}</span>
                        : <span className="text-red-600">Δ €{(totalAlloc - repairAmount).toFixed(2)}</span>}
                    </div>
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Add apartment */}
          {availableApartments.length > 0 && (
            <div className="flex items-center gap-2 border border-dashed border-zinc-300 px-3 py-2 bg-zinc-50">
              <Plus size={14} className="text-zinc-500" />
              <span className="text-xs text-zinc-500">{t('addApartment') || 'Add apartment'}:</span>
              <Select value="" onValueChange={addRow}>
                <SelectTrigger className="rounded-none h-8 w-72 ml-2" data-testid="add-apt-select">
                  <SelectValue placeholder={t('selectApartment') || 'Select apartment'} />
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {availableApartments.map(a => (
                    <SelectItem key={a.apartment_id} value={a.apartment_id}>
                      #{a.apartment_number} — {a.owner_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Audit panel */}
          {showAudit && (
            <div className="border border-zinc-200 bg-zinc-50">
              <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-white">
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  {t('changeHistory') || 'Change history'}
                </span>
                <button onClick={() => setShowAudit(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="close-audit-btn">
                  <X size={14} />
                </button>
              </div>
              {auditRows.length === 0 ? (
                <div className="px-3 py-4 text-xs text-zinc-400 text-center">{t('noHistory') || 'No edits yet.'}</div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {auditRows.map(a => (
                    <li key={a.audit_id} className="px-3 py-2 text-xs" data-testid={`audit-${a.audit_id}`}>
                      <div className="flex justify-between text-zinc-500 mb-1">
                        <span className="font-mono">{a.changed_at?.slice(0, 19).replace('T', ' ')}</span>
                        <span>{a.changed_by}</span>
                      </div>
                      {a.added?.length > 0 && (
                        <div className="text-emerald-700"><b>+ {t('added') || 'Added'}:</b> {a.added.map(x => `#${x.apartment_number} (${x.share_percentage}%)`).join(', ')}</div>
                      )}
                      {a.removed?.length > 0 && (
                        <div className="text-red-700"><b>− {t('removed') || 'Removed'}:</b> {a.removed.map(x => `#${x.apartment_number} (${x.share_percentage}%)`).join(', ')}</div>
                      )}
                      {a.changed?.length > 0 && (
                        <div className="text-amber-700"><b>~ {t('changed') || 'Changed'}:</b> {a.changed.map(x => `#${x.apartment_number}: ${x.old?.share_percentage}% → ${x.new?.share_percentage}%`).join(', ')}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-zinc-200 pt-4 mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-none" data-testid="dist-cancel-btn">
            {t('cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!balanced || rows.length === 0 || saving}
            className="bg-zinc-950 hover:bg-zinc-800 rounded-none px-6"
            data-testid="dist-save-btn"
          >
            {saving ? (t('saving') || 'Saving…') : (t('saveChanges') || t('save'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
